import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DownloadIntermediateDE, NQValueKind, PingIntermediateDE, UploadIntermediateDE } from "@visonum/network-quality-sdk";
import { AppThunk } from "../../store";
import { createNQRecord } from "./createNQRecord";
import { migrateLocalStorage } from "./migrateLocalStorage";
import { decodeFromLocalStorage } from "./decodeFromLocalStorage";
import { getHistory, setHistory } from "../../helper/storage";
import { Phase } from "../measurement/measurementSlice";
import { serializeToLocalStorage } from "./serializeToLocalStorage";
import { AppAssertionError, reportNQError } from "../errors/errorSlice";
import { NQRecord } from "./types";
import { HistoryState } from "./HistoryState";
import { SDK_KIND } from "../../config";
import { getSyntheticHistory } from "./syntheticHistory";
import { getNqError } from "../errors/errorUtils";

const initialState: HistoryState = {
  records: [],
  selectedRecords: [],
  expandedRecord: null,
  firstItemIndex: 0,
  frameItemCount: 6,
  removeHistoryDialogVisible: false,
}

export const initHistory = (): AppThunk => (dispatch, _) => {
  if (SDK_KIND === "Synthetic") {
    dispatch(updateRecords(getSyntheticHistory()));
  } else {
    migrateLocalStorage();
    const records = decodeFromLocalStorage(getHistory());
    dispatch(updateRecords(records));
  }
}

export const deleteAllRecords = (): AppThunk => (dispatch, _) => {
  dispatch(updateStore([]));
}

export const deleteSelectedRecords = (): AppThunk => (dispatch, getState) => {
  const history = getState().history;
  const newItems = history.records.filter(v => !history.selectedRecords.includes(v));
  dispatch(updateSelectedRecords([]));
  dispatch(updateStore(newItems));
}

export const deleteRecordBySpeedtestId = (speedtestId: string): AppThunk => (dispatch, getState) => {
  const newItems = getState().history.records.filter(v => v.prepareResult.init.speedtest.id !== speedtestId);
  dispatch(updateStore(newItems));
}

export const appendStoreNQValues = (): AppThunk => (dispatch, getState) => {
  try {
    const state = getState();
    const measurement = state.measurement;
    const kind = measurement.kind;

    if (kind !== Phase.CompletingNoModem &&
      kind !== Phase.FinishedNoModem &&
      kind !== Phase.CompletingWithModem &&
      kind !== Phase.FinishedWithModem) {
      throw new AppAssertionError({ actual: kind, expected: "CompletingNoModem or FinishedNoModem or CompletingWithModem or FinishedWithModem" });
    }

    const nqValues = [
      measurement.prepareResult,
      measurement.benchmark,
      ...measurement.downloadIntermediates.map(x => ({ kind: NQValueKind.DownloadIntermediate, speed: x } as DownloadIntermediateDE)),
      measurement.download,
      ...measurement.uploadIntermediates.map(x => ({ kind: NQValueKind.UploadIntermediate, speed: x } as UploadIntermediateDE)),
      measurement.upload,
      ...measurement.pingIntermediates.map(x => ({ kind: NQValueKind.PingIntermediate, pingTime: x } as PingIntermediateDE)),
      measurement.ping,
      measurement.modem,
      measurement.hints ?? { kind: NQValueKind.Hints, hints: [] },
    ];
    const nqRecord = createNQRecord(nqValues, new Date());
    const newItems = [...state.history.records, nqRecord];
    dispatch(updateStore(newItems));
  } catch (err) {
    dispatch(reportNQError(getNqError(err)));
  }
}

export const updateStore = (newItems: NQRecord[]): AppThunk => (dispatch, getState) => {
  dispatch(updateRecords(newItems));
  const serialized = serializeToLocalStorage(newItems);
  setHistory(serialized);
}

export const historySlice = createSlice({
  name: 'history',
  initialState: initialState,
  reducers: {
    updateRecords: (state, action: PayloadAction<NQRecord[]>) => {
      state.records = action.payload;
      if (state.expandedRecord !== null && !state.records.includes(state.expandedRecord)) {
        state.expandedRecord = null;
      }
    },
    updateFirstItemIndex: (state, action: PayloadAction<number>) => {
      state.firstItemIndex = action.payload;
    },
    showRemoveHistoryDialog: (state) => {
      state.removeHistoryDialogVisible = true;
    },
    hideRemoveHistoryDialog: (state) => {
      state.removeHistoryDialogVisible = false;
    },
    updateSelectedRecords: (state, action: PayloadAction<NQRecord[]>) => {
      state.selectedRecords = action.payload;
    },
    updateExpandedRecord: (state, action: PayloadAction<NQRecord | null>) => {
      state.expandedRecord = action.payload;
    },
  },
});

export const reducer = historySlice.reducer;
export const {
  updateRecords,
  updateFirstItemIndex,
  showRemoveHistoryDialog,
  hideRemoveHistoryDialog,
  updateSelectedRecords,
  updateExpandedRecord,
} = historySlice.actions;
